import { getSensorColor } from 'features/timeSeries'
import { CursorDataPoint } from 'features/timeSeries/CursorDataContext'
import {
  isFiniteNumber,
  SensorRangeMap,
} from 'features/timeSeries/utils/axisRangeHelpers'
import { useMemo } from 'react'
import {
  BaseSensorEntry,
  SensorScatterplotData,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

/**
 * Returns the current value and unit as separate strings for display.
 */
export const formatCurrentValue = (
  value: number | null | undefined,
  unit?: string
): [string, string] => {
  if (value !== null && value !== undefined) {
    return [value.toFixed(2), unit ?? '']
  }
  return ['--', unit ?? '']
}

/**
 * Format x-value for display
 */
export const formatXValue = (
  xValue: number | string | null | undefined,
  xUnit?: string
): string => {
  if (xValue === null || xValue === undefined) {
    return '--'
  }
  if (typeof xValue === 'string') {
    return xValue
  }
  const formatted = xValue.toFixed(2)
  return xUnit ? `${formatted} ${xUnit}` : formatted
}

/**
 * Hook to transform sensors and derived values into table data
 */
/**
 * A pen's displayable bound: the custom override when set, otherwise the
 * computed data bound — and undefined (never ±Infinity) when there is no
 * finite data yet, so render sites can show a no-data placeholder.
 */
const displayBound = (
  customValue: number | undefined,
  autoValue: number | undefined
): number | undefined => {
  if (customValue !== undefined) return customValue
  return isFiniteNumber(autoValue)
    ? Number(autoValue.toPrecision(4))
    : undefined
}

export const useSensorTableData = (
  selectedSensors: BaseSensorEntry[],
  derivedValues: TimeSeriesDerivedValueEntryJSON[],
  sensorRanges: SensorRangeMap,
  customAxisRanges: Record<string, { min?: number; max?: number }>,
  cursorData: Record<string, CursorDataPoint>,
  customSensorColors: Record<string, string>,
  xAxisSensorId: string | null,
  unitMapping: Record<string, string>,
  sensorEntries: BaseSensorEntry[]
): SensorScatterplotData[] => {
  return useMemo(() => {
    // Calculate x-axis unit
    let xUnit: string | undefined
    if (xAxisSensorId) {
      if (xAxisSensorId.startsWith('derived_')) {
        const derivedValue = derivedValues.find((dv) => dv.id === xAxisSensorId)
        xUnit = derivedValue?.data.unit || undefined
      } else {
        const sensor = sensorEntries.find((e) => e.id === xAxisSensorId)
        if (sensor) {
          const sensorName = sensor.data.name
          const unit = unitMapping[sensorName]
          xUnit = unit && unit !== 'No Unit' ? unit : undefined
        }
      }
    }

    // Map sensors to table data
    const sensorData: SensorScatterplotData[] = selectedSensors.map(
      (sensor, sensorIndex) => {
        const cursorInfo = cursorData[sensor.id]
        return {
          id: sensor.id,
          name: sensor.data.name,
          description: sensor.data.description || '',
          yMin: displayBound(
            customAxisRanges[sensor.id]?.min,
            sensorRanges[sensor.id]?.min
          ),
          yMax: displayBound(
            customAxisRanges[sensor.id]?.max,
            sensorRanges[sensor.id]?.max
          ),
          color: customSensorColors[sensor.id] ?? getSensorColor(sensorIndex),
          isDerived: false,
          currentValue: cursorInfo?.yValue || null,
          unit:
            cursorInfo?.unit ||
            sensor.data.unit ||
            sensorRanges[sensor.id]?.unit,
          xValue: cursorInfo?.xValue || null,
          xUnit,
        }
      }
    )

    // Map derived values to table data - only include those with all dependencies selected
    const derivedData: SensorScatterplotData[] = derivedValues
      .filter((derived) => {
        // Only include derived values where all dependencies are selected
        return derived.data.dependencyIds.every((sensorId) =>
          selectedSensors.some((selected) => selected.id === sensorId)
        )
      })
      .map((derived, derivedIndex) => {
        const cursorInfo = cursorData[derived.id]
        return {
          id: derived.id,
          name: derived.data.name,
          description:
            derived.data.description || `Derived: ${derived.data.name}`,
          yMin: displayBound(
            customAxisRanges[derived.id]?.min,
            sensorRanges[derived.id]?.min
          ),
          yMax: displayBound(
            customAxisRanges[derived.id]?.max,
            sensorRanges[derived.id]?.max
          ),
          color:
            customSensorColors[derived.id] ??
            getSensorColor(selectedSensors.length + derivedIndex),
          isDerived: true,
          currentValue: cursorInfo?.yValue || null,
          unit:
            cursorInfo?.unit ||
            derived.data.unit ||
            sensorRanges[derived.id]?.unit,
          xValue: cursorInfo?.xValue || null,
          xUnit,
        }
      })

    // Combine sensors and derived values
    return [...sensorData, ...derivedData]
  }, [
    selectedSensors,
    derivedValues,
    sensorRanges,
    customAxisRanges,
    cursorData,
    customSensorColors,
    xAxisSensorId,
    unitMapping,
    sensorEntries,
  ])
}
